/**
 * Cart Service
 *
 * Handles all shopping cart-related API calls
 */

import api from './api';

export const cartService = {
  /**
   * Get cart
   * @returns {Promise<Object>} Cart with items and summary
   */
  getCart: async () => {
    try {
      const response = await api.get('/cart');
      return response.data;
    } catch (error) {
      console.error('[CartService] Get cart error:', error);
      throw error;
    }
  },

  /**
   * Add item to cart
   * @param {Object} item - Cart item data
   * @param {number} item.productId - Product ID
   * @param {number} item.quantity - Quantity to add
   * @param {Object} item.personalization - Personalization options
   * @returns {Promise<Object>} Updated cart
   */
  addToCart: async (item) => {
    try {
      const response = await api.post('/cart', item);
      return response.data;
    } catch (error) {
      console.error('[CartService] Add to cart error:', error);
      throw error;
    }
  },

  /**
   * Update cart item
   * @param {number} itemId - Cart item ID
   * @param {Object} updates - Updates to apply
   * @param {number} updates.quantity - New quantity
   * @param {Object} updates.personalization - Updated personalization
   * @returns {Promise<Object>} Updated cart
   */
  updateCartItem: async (itemId, updates) => {
    try {
      const response = await api.put(`/cart/${itemId}`, updates);
      return response.data;
    } catch (error) {
      console.error('[CartService] Update cart item error:', error);
      throw error;
    }
  },

  /**
   * Remove item from cart
   * @param {number} itemId - Cart item ID
   * @returns {Promise<Object>} Updated cart
   */
  removeFromCart: async (itemId) => {
    try {
      const response = await api.delete(`/cart/${itemId}`);
      return response.data;
    } catch (error) {
      console.error('[CartService] Remove from cart error:', error);
      throw error;
    }
  },

  /**
   * Clear cart
   * @returns {Promise<Object>} Empty cart
   */
  clearCart: async () => {
    try {
      const response = await api.delete('/cart');
      return response.data;
    } catch (error) {
      console.error('[CartService] Clear cart error:', error);
      throw error;
    }
  },

  /**
   * Update item quantity
   * @param {number} itemId - Cart item ID
   * @param {number} quantity - New quantity
   * @returns {Promise<Object>} Updated cart
   */
  updateQuantity: async (itemId, quantity) => {
    try {
      const response = await api.put(`/cart/${itemId}`, { quantity });
      return response.data;
    } catch (error) {
      console.error('[CartService] Update quantity error:', error);
      throw error;
    }
  },

  /**
   * Add personalization to cart item
   * @param {number} itemId - Cart item ID
   * @param {Object} personalization - Personalization data
   * @returns {Promise<Object>} Updated cart
   */
  addPersonalization: async (itemId, personalization) => {
    try {
      const response = await api.put(`/cart/${itemId}`, { personalization });
      return response.data;
    } catch (error) {
      console.error('[CartService] Add personalization error:', error);
      throw error;
    }
  }
};

export default cartService;
